import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";
import z from "zod";

import NavBarTitle from "@onsite_app/MobileOrderingMenu/Atoms/Navbar";

import {
  Button,
  LinkButton,
  Price,
  PrimaryAction,
  useSubmitButton,
} from "@online_ordering/Atoms";
import { Label, TextField } from "@online_ordering/Atoms/Form";

import { useVenue } from "../../VenueContext";
import { VoucherCardPreview } from "./VoucherCartItems/VoucherCardPreview";
import VoucherCartItemsQueries from "./VoucherCartItems/queries";
import { convertToMinimalVoucher } from "./VoucherCartItems/schema";
import { VoucherPricing } from "./VoucherTemplates/VoucherTemplateCard";
import { useCreateVoucherCheckout, useUpdateVoucherCart } from "./mutations";
import { VoucherCartQueries } from "./queries";

const VoucherCartFormSchema = z.object({
  purchaserEmail: z.string().email("Please enter a valid email address"),
});

type VoucherCartForm = z.infer<typeof VoucherCartFormSchema>;
const VoucherCheckout = () => {
  const { t } = useTranslation("onsite", {
    keyPrefix: "voucherCartCheckout",
  });

  const voucherCartQuery = useSuspenseQuery(VoucherCartQueries.current);
  const voucherCart = voucherCartQuery.data;
  const voucherCartItems = useSuspenseQuery(
    VoucherCartItemsQueries.cartItems(voucherCart.uuid),
  ).data;
  const venue = useVenue();
  const [submissionState, trackSubmit] = useSubmitButton();

  const formProps = useForm<VoucherCartForm>({
    resolver: zodResolver(VoucherCartFormSchema),
    defaultValues: {
      purchaserEmail: voucherCart.purchaserEmail || "",
    },
  });
  const updateVoucherCartMutation = useUpdateVoucherCart();

  const onCartUpdate = () => {
    updateVoucherCartMutation.mutate({
      uuid: voucherCart.uuid,
      ...formProps.getValues(),
    });
  };
  const createVoucherCartCheckoutMutation = useCreateVoucherCheckout();
  const onSubmit = async () => {
    onCartUpdate();
    const { checkoutUrl } = await createVoucherCartCheckoutMutation.mutateAsync(
      voucherCart.uuid,
    );
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };

  const checkoutTotal = voucherCartItems.reduce(
    (total, item) => total + item.priceInCents,
    0,
  );

  return (
    <div>
      <NavBarTitle title={t("title")} />
      <div className="mx-auto max-w-screen-sm space-y-24">
        {voucherCartQuery.data ? (
          <div className="space-y-32 pb-12">
            <form onSubmit={formProps.handleSubmit(onCartUpdate)}>
              <TextField
                label={t("yourEmailAddress")}
                helpText={t("yourEmailAddress_helpText")}
                placeholder={t("yourEmailAddress_placeholder")}
                formProps={formProps}
                name="purchaserEmail"
                type="email"
                onBlur={onCartUpdate}
              />
            </form>
            <div className="space-y-32">
              {voucherCartItems && voucherCartItems.length > 0 ? (
                <>
                  <Label>
                    {t("vouchersToPurchase", {
                      count: voucherCartItems.length,
                    })}
                  </Label>

                  <div className="space-y-32">
                    {voucherCartItems.map((item) => (
                      <div key={item.uuid} className="space-y-8">
                        <Link to={`../items/${item.uuid}`}>
                          <VoucherCardPreview
                            voucher={convertToMinimalVoucher(item, venue.name)}
                            key={item.uuid}
                          />
                        </Link>
                        <div className="mx-auto flex max-w-xs items-baseline justify-between space-x-8">
                          <div>
                            <Link
                              className="text-lg"
                              to={`../items/${item.uuid}`}
                            >
                              {t("editVoucher")}
                            </Link>
                          </div>
                          <div className="flex-1 text-right text-2xl">
                            <VoucherPricing
                              valueInCents={item.valueInCents}
                              priceInCents={item.priceInCents}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p>{t("noVouchersInCart")}</p>
              )}
              <LinkButton secondary to="../">
                {t("addMoreVouchers")}
              </LinkButton>
              <div className="hidden items-center justify-between space-x-32 md:flex">
                <div className="flex-none text-2xl font-extrabold">
                  <Price showCurrency fromCents value={checkoutTotal} />
                </div>
                <div className="flex-1">
                  <Button
                    onClick={formProps.handleSubmit(trackSubmit(onSubmit))}
                    disabled={voucherCartItems.length === 0}
                    submissionState={submissionState}
                    submissionOptions={{
                      loadingMessage: t("processingYourCheckout"),
                    }}
                    primary
                  >
                    {t("checkoutNow")}
                  </Button>
                </div>
              </div>
            </div>
            <PrimaryAction>
              <Button
                onClick={formProps.handleSubmit(trackSubmit(onSubmit))}
                disabled={voucherCartItems.length === 0}
                primary
                submissionState={submissionState}
                submissionOptions={{
                  loadingMessage: t("processingYourCheckout"),
                }}
              >
                {t("checkoutNow")}
                <Price
                  showDecimals
                  showCurrency
                  fromCents
                  value={checkoutTotal}
                />
              </Button>
            </PrimaryAction>
          </div>
        ) : (
          <p>{t("noVoucherCart")}</p>
        )}
      </div>
    </div>
  );
};

export default VoucherCheckout;
